import React from 'react';
import { Typography } from '../../atoms/Typography';
import { makeCSS } from '../../../utils/makeCSS';
import { Contract } from '@cube3/common/model/schema/resources/contract';
import { Status, StatusType } from '../../atoms/Status/Status';
import Time from '../../utility/Time';
import Shimmer from '../../atoms/Loader/Shimmer';
import { Tag } from '../../atoms/Tag/Tag';
import { Tooltip } from '../../atoms/Tooltip/Tooltip';
import { SizeVariants } from '../../../theme/themes';
import { SuggestionItemStatus } from './AccountSuggestionItem';

interface Attachment {
  name: string;
  type: string;
  onClick?(ev: React.MouseEvent): void;
}

export interface ContractSuggestionItemProps {
  item: Contract & { __contractType: string } & SuggestionItemStatus;
  showDate?: boolean;
  showAttachments?: boolean;
  attachments?: Attachment[];
  /** default is 31 */
  expiresSoonOffsetDay?: number;
}

const useCSS = makeCSS(({ theme }) => ({
  root: {},
  selected: {
    background: theme.color.overlay.pressed
  },
  interactive: {
    // opacity: 0.8,
    cursor: 'pointer',
    '&:hover': {
      opacity: 1
      // background: theme.customPalette.backgroundAccent.whiteAccent1
    },
    '&:active': {
      background: theme.color.overlay.hovered
    }
  },
  chips: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 8
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: 10
    }
  },
  meta: {}
}));

export const ContractTitle = ({ contract, expiresSoonOffsetDay = 31 }) => {
  const status = getContractStatus(contract, expiresSoonOffsetDay);
  const classes = useCSS();
  return (
    <div className={classes.header}>
      <Tooltip
        content={
          <Typography variant="md" colorVariant="01">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Typography>
        }
        useArrow={false}
      >
        <Status status={status} useText={false} />
      </Tooltip>

      <Typography weight="label" colorVariant="01" variant={SizeVariants.lg}>
        {contract.display_name}
      </Typography>
    </div>
  );
};

export const ContractSuggestionItem = (props: ContractSuggestionItemProps) => {
  const { item, attachments, showAttachments, showDate, expiresSoonOffsetDay } =
    props;

  const { starts_at, expires_at } = item;

  let dateLabel;
  let date;
  if (starts_at || expires_at) {
    if (starts_at && new Date(starts_at) > new Date()) {
      date = starts_at;
      dateLabel = 'Starting at';
    } else if (expires_at && new Date(expires_at) < new Date()) {
      date = expires_at;
      dateLabel = 'Expired on';
    } else {
      date = expires_at;
      dateLabel = 'Valid until';
    }
  }

  const classes = useCSS();
  if (!item) return null;
  return (
    <div className={classes.root} onClick={item.onClick}>
      <ContractTitle
        contract={item}
        expiresSoonOffsetDay={expiresSoonOffsetDay}
      />

      <div className={classes.meta}>
        <Typography colorVariant="02" weight="body">
          {item.__contractType || '...'}
        </Typography>
        {showDate && date && (
          <>
            <Typography colorVariant="02" weight="body">
              {' '}
              •{' '}
            </Typography>
            <Typography colorVariant="02" weight="body">
              {dateLabel}{' '}
              <Time
                date={new Date(date)}
                noTime={true}
                toolTip={false}
                placement="top"
              />
            </Typography>
          </>
        )}
      </div>

      {showAttachments && (
        <div className={classes.chips}>
          {/* <pre>{JSON.stringify(attachments, null, 2)}</pre> */}
          {!attachments && (
            <Shimmer
              width={160}
              height={36}
              margin="medium"
              variant="rectangle"
              weight="light"
            />
          )}
          {attachments?.length > 0 &&
            attachments.map((a) => {
              const isProject = a.type === 'project';
              return (
                <Tag
                  key={a.name}
                  label={a.name}
                  iconLeft={isProject ? 'project' : undefined}
                  color={isProject ? 'gray' : 'overlayColor'}
                  chipStyle={'filled'}
                  onClick={a.onClick}
                />
              );
            })}
          {attachments && attachments.length === 0 && (
            <Typography colorVariant="03" weight="body">
              Not attached to assets or projects
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export const getContractStatus = (
  contract: Contract,
  expiresSoonOffsetDay = 31
): StatusType => {
  if (contract.expires && new Date(contract.starts_at) > new Date()) {
    return 'not started';
  }

  if (contract.expires && new Date(contract.expires_at) < new Date()) {
    return 'expired';
  }

  if (
    contract.expires_soon !== undefined
      ? contract.expires_soon
      : contract.expires &&
        new Date(contract.expires_at) <
          new Date(Date.now() + 1000 * 60 * 60 * 24 * expiresSoonOffsetDay)
  ) {
    return 'expires soon';
  }

  if (contract.extended) {
    return 'active';
  }

  return 'active';
};
